import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  X,
  Check,
  CheckCheck,
  Phone,
  MessageSquare,
  Mail,
  Smartphone,
  Megaphone,
  CreditCard,
  Shield,
  Settings,
  ExternalLink,
  Trash2,
} from 'lucide-react';
import { notificationService } from 'api/services/settings.service';
import { NotificationDTO, NotificationCategory, NotificationType } from 'types/settings';

/**
 * @param placement where the panel opens relative to the bell.
 *
 * "below" is the original: the bell sat in a top bar with the whole page under
 * it. "side" is for the sidebar footer, where dropping down would open off the
 * bottom of the screen and a 24rem panel would overflow a 16rem rail sideways.
 * OrganizationSwitcher, three elements above it, already opens that way.
 */
const NotificationDropdown: React.FC<{
  placement?: 'below' | 'side' | 'side-top';
  /**
   * How the trigger is drawn.
   *
   * "side" hangs the panel off the right edge, bottom-aligned, for a trigger
   * near the foot of the sidebar; "side-top" top-aligns it for one near the
   * head. Either way it goes sideways: a 24rem panel cannot open inside a 16rem
   * rail, and dropping down from the bottom opens off-screen.
   *
   * "icon" is a round button, which is what a top bar wants. "row" is a nav
   * line — icon, label, count — which is what the sidebar footer already uses
   * for Settings and Help & Support. A round button dropped into that list
   * reads as a foreign object, which is exactly how the first two attempts at
   * rehoming this looked.
   */
  variant?: 'icon' | 'row';
  /** Collapsed sidebar: the label is hidden and only the icon is drawn. */
  expanded?: boolean;
}> = ({ placement = 'below', variant = 'icon', expanded = true }) => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<NotificationDTO[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchUnreadCount();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationService.list({ size: 10 });
      setNotifications(response.content);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await notificationService.markAsRead(id);
      setNotifications(
        notifications.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(notifications.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await notificationService.delete(id);
      const notification = notifications.find((n) => n.id === id);
      setNotifications(notifications.filter((n) => n.id !== id));
      if (notification && !notification.read) {
        setUnreadCount(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const handleNotificationClick = (notification: NotificationDTO) => {
    if (!notification.read) {
      notificationService.markAsRead(notification.id);
      setNotifications(
        notifications.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );
      setUnreadCount(Math.max(0, unreadCount - 1));
    }
    if (notification.link) {
      navigate(notification.link);
    }
    setIsOpen(false);
  };

  const getCategoryIcon = (category: NotificationCategory) => {
    const icons = {
      [NotificationCategory.CALL]: Phone,
      [NotificationCategory.SMS]: Mail,
      [NotificationCategory.WHATSAPP]: MessageSquare,
      [NotificationCategory.RCS]: Smartphone,
      [NotificationCategory.CAMPAIGN]: Megaphone,
      [NotificationCategory.BILLING]: CreditCard,
      [NotificationCategory.SECURITY]: Shield,
      [NotificationCategory.SYSTEM]: Settings,
    };
    return icons[category] || Bell;
  };

  const getTypeColor = (type: NotificationType) => {
    const colors = {
      [NotificationType.INFO]: 'bg-blue-100 text-blue-600',
      [NotificationType.SUCCESS]: 'bg-green-100 text-green-600',
      [NotificationType.WARNING]: 'bg-yellow-100 text-yellow-600',
      [NotificationType.ERROR]: 'bg-red-100 text-red-600',
      [NotificationType.ALERT]: 'bg-purple-100 text-purple-600',
    };
    return colors[type] || 'bg-gray-100 text-gray-600';
  };

  const formatTime = (date: string) => {
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now.getTime() - notifDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifDate.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      {variant === 'row' ? (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`tl-nav-item ${expanded ? '' : 'justify-center'}`}
          title={!expanded ? 'Notifications' : undefined}
        >
          <span className="relative flex-shrink-0">
            <Bell className="w-[18px] h-[18px]" />
            {!expanded && unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
            )}
          </span>
          {expanded && <span className="flex-1">Notifications</span>}
          {expanded && unreadCount > 0 && (
            /* The same pill shape the nav already uses for "Locked", so a count
               reads as part of the list rather than as a badge stuck on it. */
            <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-red-500 text-white font-semibold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      ) : (
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="tl-icon-btn relative flex-shrink-0"
          style={{ width: 34, height: 34 }}
          title="Notifications"
        >
          <Bell className="w-4 h-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div
          className={`absolute w-96 shadow-xl tl-card-flush ${
            placement === 'side' ? 'left-full ml-2 bottom-0 z-[9999]'
              : placement === 'side-top' ? 'left-full ml-2 top-0 z-[9999]'
              : 'right-0 mt-2 z-50'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h3 className="font-semibold text-gray-900">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <CheckCheck className="w-4 h-4" />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 mx-auto mb-2 opacity-20" />
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = getCategoryIcon(notification.category);
                return (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`flex items-start gap-3 p-4 border-b cursor-pointer hover:bg-gray-50 transition ${
                      !notification.read ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <div className={`p-2 rounded-lg ${getTypeColor(notification.type)}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notification.read ? 'font-semibold' : ''}`}>
                          {notification.title}
                        </p>
                        <span className="text-xs text-gray-400 whitespace-nowrap">
                          {formatTime(notification.createdAt)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1">
                      {!notification.read && (
                        <button
                          onClick={(e) => handleMarkAsRead(notification.id, e)}
                          className="p-1 text-gray-400 hover:text-green-600"
                          title="Mark as read"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => handleDelete(notification.id, e)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t">
            <button
              onClick={() => {
                navigate('/notifications');
                setIsOpen(false);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
            >
              View all notifications
              <ExternalLink className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
